import * as React from "react";
import { useNavigate } from "react-router-dom";
import {
    Box, Button, Card, CardContent, CardMedia, Typography,
    Grid, TablePagination, Stack, Dialog,
    DialogTitle, DialogContent, DialogContentText, DialogActions, Tabs, Tab
} from "@mui/material";
import DeleteIcon from "@mui/icons-material/Delete";
import { deleteAlbum, getInfoGraphics } from "../../../Services/ApiCalls";
import type { Album } from "../../../types";

// Category mapping: Display name -> Database category value
// Based on how albums are filtered in public pages
const CATEGORY_MAP: Record<string, string> = {
    "InfoGraphics": "RadioInfo",
    "Publications": "Mypub",
    "RadioIllustrations": "RadioIllust",
    "ShortVideos": "ShortVideo",
};

const CATEGORIES = Object.keys(CATEGORY_MAP);

export default function AdminAlbumsList() {
    const navigate = useNavigate();
    const [category, setCategory] = React.useState<string>("InfoGraphics");
    const [page, setPage] = React.useState(0);
    const [rowsPerPage, setRowsPerPage] = React.useState(10);
    const [allAlbums, setAllAlbums] = React.useState<Album[]>([]); // All albums for current category
    const [total, setTotal] = React.useState(0);
    const [loading, setLoading] = React.useState(false);
    const [deleteDialog, setDeleteDialog] = React.useState<{ open: boolean; album: Album | null }>({
        open: false,
        album: null,
    });
    const [deleting, setDeleting] = React.useState(false);

    const fetchAlbums = React.useCallback(async () => {
        try {
            setLoading(true);
            // Fetch a large batch of albums (like public pages do)
            // We'll filter by category and paginate client-side
            const response = await getInfoGraphics(1, 1000); // Fetch large batch
            
            // Filter albums by the selected category
            const dbCategory = CATEGORY_MAP[category];
            const filtered = (response.albums || []).filter(
                (album: Album) => album.category === dbCategory
            );
            
            setAllAlbums(filtered);
            setTotal(filtered.length);
        } catch (error) {
            console.error("Error fetching albums:", error);
            setAllAlbums([]);
            setTotal(0);
        } finally {
            setLoading(false);
        }
    }, [category]);

    React.useEffect(() => {
        fetchAlbums();
    }, [fetchAlbums]);

    const handleDeleteClick = (album: Album) => {
        setDeleteDialog({ open: true, album });
    };

    const handleDeleteConfirm = async () => {
        if (!deleteDialog.album) return;
        try {
            setDeleting(true);
            await deleteAlbum(deleteDialog.album.id);
            setDeleteDialog({ open: false, album: null });
            fetchAlbums(); // Refresh the list
        } catch (error: any) {
            console.error("Error deleting album:", error);
            const errorMessage = error?.message || "Failed to delete album";
            if (errorMessage.includes("401") || errorMessage.includes("Authentication")) {
                alert("Authentication failed. Please log in again and try again.");
            } else {
                alert(`Failed to delete album: ${errorMessage}`);
            }
        } finally {
            setDeleting(false);
        }
    };

    const handleDeleteCancel = () => {
        setDeleteDialog({ open: false, album: null });
    };

    return (
        <Box sx={{ p: 2 }}>
            <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 2 }}>
                <Tabs value={category} onChange={(_, v) => { setCategory(v); setPage(0); }} aria-label="category tabs">
                    {CATEGORIES.map((cat) => (
                        <Tab key={cat} label={cat} value={cat} />
                    ))}
                </Tabs>
            </Stack>

            {loading && <Typography>Loading…</Typography>}

            {!loading && allAlbums.length === 0 && (
                <Box sx={{ p: 4, textAlign: "center" }}>
                    <Typography variant="h6" color="text.secondary">
                        No albums found in {category}
                    </Typography>
                </Box>
            )}

            {!loading && allAlbums.length > 0 && (
                <Grid container spacing={2}>
                {allAlbums
                    .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                    .map((album) => (
                    <Grid key={album.id} size={{ xs: 12, md: 6, lg: 4 }}>
                        <Card sx={{ height: "100%", display: "flex", flexDirection: "column" }}>
                            {album.coverImage && (
                                <CardMedia
                                    component="img"
                                    image={album.coverImage}
                                    alt={album.title}
                                    sx={{ height: 180, objectFit: "cover" }}
                                />
                            )}
                            <CardContent sx={{ flex: 1 }}>
                                <Typography variant="h6">{album.title}</Typography>
                                {album.description && (
                                    <Typography variant="body2" sx={{ mt: 1 }}>{album.description}</Typography>
                                )}
                                <Typography variant="caption" sx={{ mt: 1, display: "block" }}>
                                    Category: {album.category} • Created: {new Date(album.createdAt).toLocaleDateString()}
                                </Typography>
                            </CardContent>
                            <Stack direction="row" spacing={1} sx={{ p: 2, pt: 0 }}>
                                <Button
                                    variant="outlined"
                                    onClick={() => navigate(`/Album/${album.id}`)}
                                >
                                    View
                                </Button>
                                <Button
                                    variant="outlined"
                                    color="error"
                                    startIcon={<DeleteIcon />}
                                    onClick={() => handleDeleteClick(album)}
                                >
                                    Delete
                                </Button>
                            </Stack>
                        </Card>
                    </Grid>
                ))}
            </Grid>
            )}

            {!loading && allAlbums.length > 0 && (
            <TablePagination
                component="div"
                count={total}
                page={page}
                onPageChange={(_, p) => setPage(p)}
                rowsPerPage={rowsPerPage}
                onRowsPerPageChange={(e) => {
                    setRowsPerPage(parseInt(e.target.value, 10));
                    setPage(0);
                }}
            />
            )}

            <Dialog open={deleteDialog.open} onClose={handleDeleteCancel}>
                <DialogTitle>Delete Album</DialogTitle>
                <DialogContent>
                    <DialogContentText>
                        Are you sure you want to delete "{deleteDialog.album?.title}"? This action cannot be undone.
                        All associated photos will also be deleted from S3.
                    </DialogContentText>
                </DialogContent>
                <DialogActions>
                    <Button onClick={handleDeleteCancel} disabled={deleting}>
                        Cancel
                    </Button>
                    <Button onClick={handleDeleteConfirm} color="error" variant="contained" disabled={deleting}>
                        {deleting ? "Deleting..." : "Delete"}
                    </Button>
                </DialogActions>
            </Dialog>
        </Box>
    );
}

